<template>
  <b-modal
    :title="helper.trans('statistic.counterparties.view_stats_title')"
    dialog-class="modal-lg"
    :modelValue="isVisible"
    footer-class="d-flex justify-content-end"
    @update:modelValue="isVisible = $event"
  >
    <template #default>
      <div v-if="item" class="p-2">
        <dl class="row mb-0 small">
          <template v-for="field in fields" :key="field.key">
            <dt class="col-sm-5 text-muted text-capitalize-first">{{ field.label }}</dt>
            <dd class="col-sm-7">{{ field.value(item) }}</dd>
          </template>
        </dl>
      </div>
    </template>
    <template #footer>
      <button type="button" @click.prevent="hide" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.ok') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue';
import {BModal} from 'bootstrap-vue-3';
import {helper} from '@/service/CommonHelper';
import {dateHelper} from '@/service/DateHelper';
import {numberFormatter} from '@/service/NumberFormatter';
import CounterpartyStatisticsDTO from '@/models/CounterpartyStatisticsDTO';

export default defineComponent({
  components: {BModal},
  setup() {
    const isVisible = ref(false);
    const item = ref<CounterpartyStatisticsDTO | null>(null);

    const fields = [
      {key: 'userId', label: helper.trans('labels.user_id'), value: (row: CounterpartyStatisticsDTO) => row.userId},
      {key: 'storeId', label: helper.trans('labels.store'), value: (row: CounterpartyStatisticsDTO) => row.storeId ?? '—'},
      {key: 'totalAmount', label: helper.trans('statistic.counterparties.sent'), value: formatSent},
      {key: 'useCount', label: helper.trans('labels.use_count'), value: (row: CounterpartyStatisticsDTO) => numberFormatter.formatNumber(row.useCount)},
      {key: 'completedCount', label: helper.trans('labels.completed_count'), value: (row: CounterpartyStatisticsDTO) => numberFormatter.formatNumber(row.completedCount)},
      {key: 'totalCompleted', label: helper.trans('labels.total_completed'), value: (row: CounterpartyStatisticsDTO) => numberFormatter.formatNumber(row.totalCompleted ?? row.completedCount)},
      {key: 'totalUseCount', label: helper.trans('labels.total_use_count'), value: (row: CounterpartyStatisticsDTO) => row.totalUseCount != null ? numberFormatter.formatNumber(row.totalUseCount) : '—'},
      {key: 'conversionRate', label: helper.trans('labels.conversion_rate'), value: (row: CounterpartyStatisticsDTO) => `${numberFormatter.formatNumber(row.conversionRate, 2)}%`},
      {key: 'totalConversionRate', label: helper.trans('labels.total_conversion_rate'), value: (row: CounterpartyStatisticsDTO) => row.totalConversionRate != null ? `${numberFormatter.formatNumber(row.totalConversionRate, 2)}%` : '—'},
      {key: 'averageCheck', label: helper.trans('labels.average_check'), value: (row: CounterpartyStatisticsDTO) => numberFormatter.formatNumber(Number.parseFloat(row.averageCheck), 2)},
      {key: 'daysSinceLastUse', label: helper.trans('labels.days_since_last_use'), value: (row: CounterpartyStatisticsDTO) => numberFormatter.formatNumber(row.daysSinceLastUse)},
      {key: 'isBanned', label: helper.trans('labels.is_banned'), value: (row: CounterpartyStatisticsDTO) => row.isBanned ? helper.trans('labels.banned') : '—'},
      {key: 'firstUsedAt', label: helper.trans('labels.first_used_at'), value: (row: CounterpartyStatisticsDTO) => row.firstUsedAt ? dateHelper.format(row.firstUsedAt, 'YYYY-MM-DD') : '—'},
      {key: 'lastUsedAt', label: helper.trans('labels.last_used_at'), value: (row: CounterpartyStatisticsDTO) => formatLastUsed(row.lastUsedAt)},
    ];

    function formatSent(row: CounterpartyStatisticsDTO): string {
      if (row.totalAmount == null || row.totalAmount === '') {
        return '—';
      }
      const currency = row.currency ?? '';
      return currency ? `${row.totalAmount} ${currency}` : row.totalAmount;
    }

    function formatLastUsed(date: string): string {
      return dateHelper.format(date, 'YYYY-MM-DD HH:mm');
    }

    const show = (row: CounterpartyStatisticsDTO) => {
      item.value = row;
      isVisible.value = true;
    };

    const hide = () => {
      isVisible.value = false;
      item.value = null;
    };

    return {isVisible, item, fields, show, hide, helper};
  },
});
</script>
